import { useContext } from 'react';
import { PaymentContext } from '../providers/paymentContext';
import { calculateTotal } from '../util';
import useCreditor from './useCreditor';
import useUnpaidInvoices from './useUnpaidInvoices';
import useRates from './useRates';
import { PaymentTimeAdjustmentState } from '../util/constants';
import { calculateFeesAndDiscounts } from '../util';
import useEarlyLateFeesDetails from './useEarlyLateFeesDetails';

function isObjectAndHasProperties(obj) {
  return typeof obj === 'object' && obj !== null && Object.keys(obj).length > 0;
}

function useSelectedTotal(paying = false) {
  const { invoices } = useUnpaidInvoices();
  const { baseCurrency } = useCreditor();
  const {
    paymentOptions: { selectedInvoiceIds, payingInvoiceIds },
  } = useContext(PaymentContext);
  const invoiceIds = paying ? payingInvoiceIds : selectedInvoiceIds;
  const rates = useRates();
  const {
    doWeSetEarlyPaymentsInAccountingSystem,
    doWeSetLateFeesInAccountingSystem,
  } = useEarlyLateFeesDetails();
  const selectedFullInvoices = invoices ? invoices.filter(i => invoiceIds[i._id] === true) : [];
  const invoicesToAnalyse = invoices ?? [];

  const selectedPartialInvoices = invoicesToAnalyse
    .filter(inv => isObjectAndHasProperties(invoiceIds[inv._id]))
    .map(inv => {
      const { instalments } = inv.paymentPlan;
      const selected = invoiceIds[inv._id];
      const numberOfInstalments = Object.keys(selected).length;
      const amount = instalments
        .filter((_, i) => selected[i])
        .reduce((acc, cur) => acc + cur.amountDue, 0);

      return {
        _id: inv._id,
        InvoiceNumber: inv.InvoiceNumber,
        CurrencyCode: inv.CurrencyCode,
        instalmentsTotal: amount,
        numberOfInstalments,
        isPartial: true,
      };
    });

  const numberOfInstalments = selectedPartialInvoices.reduce(
    (acc, cur) => acc + cur.numberOfInstalments,
    0,
  );

  const combined = [...selectedPartialInvoices, ...selectedFullInvoices];
  const [combinedTotal, combinedCurrency] = calculateTotal(combined, rates, baseCurrency);
  const [fullInvoicesTotal] = calculateTotal(selectedFullInvoices, rates, baseCurrency);

  const invoiceFeesEnabled = combined.reduce(
    (acc, cur) =>
      (acc +=
        (cur.latePaymentFeesValues?.state &&
          cur.latePaymentFeesValues?.state === PaymentTimeAdjustmentState.VALID) ||
        false),
    false,
  );
  const invoiceEarlyPaymentsEnabled = combined.reduce(
    (acc, cur) =>
      (acc +=
        (cur.earlyPaymentDiscountValues?.state &&
          cur.earlyPaymentDiscountValues?.state === PaymentTimeAdjustmentState.VALID) ||
        false),
    false,
  );

  const [totalFeesCents, feesInvoiceNumbers] = calculateFeesAndDiscounts(combined, 'fee');

  const [totalDiscountsCents, discountsInvoiceNumbers] = calculateFeesAndDiscounts(
    combined,
    'discount',
  );

  const totalDiscountValueWithCondition = !doWeSetEarlyPaymentsInAccountingSystem
    ? totalDiscountsCents / 100
    : 0;
  const totalFeesValueWithCondition = !doWeSetLateFeesInAccountingSystem ? totalFeesCents / 100 : 0;

  const feesAndDiscounts = totalFeesValueWithCondition - totalDiscountValueWithCondition;

  const paymentAdjustmentsLineItemsTotal = combined.reduce((acc, cur) => {
    return acc + cur.paymentAdjustmentsLineItemsTotal;
  }, 0);

  return {
    invoices: combined,
    numberOfInvoices: combined.length,
    fullInvoices: selectedFullInvoices,
    fullInvoicesAmount: fullInvoicesTotal,
    numberOfInstalments,
    amount: combinedTotal,
    amountWithFeesAndDiscounts: combinedTotal + feesAndDiscounts,
    currency: combinedCurrency,
    feesAndDiscounts,
    totalFeesFromAllInvoices: totalFeesCents / 100,
    totalDiscountsFromAllInvoices: totalDiscountsCents / 100,
    invoiceFeesEnabled,
    invoiceEarlyPaymentsEnabled,
    doWeSetEarlyPaymentsInAccountingSystem,
    doWeSetLateFeesInAccountingSystem,
    invoicesWithFees: feesInvoiceNumbers,
    invoicesWithDiscounts: discountsInvoiceNumbers,
    paymentAdjustmentsLineItemsTotal,
  };
}

export default useSelectedTotal;
